import { useRef, useEffect } from 'react';
import { Button, Tooltip, Divider } from '@scale/scaleui';
import { usePauseCapability } from 'react-scale/components/scalers/tasks/common/PauseCapabilityHook';
import ToolTipText from 'react-scale/components/Expert/Earnings/components/TooltipText';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { formatTime, TIME_FORMAT_PRESETS } from '@scale/shared/utils/dateUtils';

interface PauseButtonProps {
  taskId?: string;
  pauseTimer?: () => void;
  startTimer?: () => void;
}

const AUTO_PAUSE_IDLE_TIME = 10 * 60 * 1000;

const PauseButton = ({ taskId }: PauseButtonProps) => {
  const lastInteraction = useRef(new Date());

  const pauseTimerClaimTimeExtensionEnabled = useUnleashFeatureFlag(
    'pause-timer-claim-time-extension',
    {},
    false,
  );

  const {
    isPaused,
    handlePauseClick,
    isLoading,
    disabledAutoPause,
    totalPausedTimeSeconds,
    pauseTimeClaimTimeExtensionInfo,
  } = usePauseCapability(taskId ?? '');

  useEffect(() => {
    const interval = setInterval(() => {
      if (
        new Date().getTime() - lastInteraction.current.getTime() >= AUTO_PAUSE_IDLE_TIME &&
        !isPaused &&
        !disabledAutoPause
      ) {
        handlePauseClick();
      }
    }, 5000);
    const moveListener = () => {
      lastInteraction.current = new Date();
    };
    window.addEventListener('mousemove', moveListener);
    window.addEventListener('keydown', moveListener);
    return () => {
      window.removeEventListener('mousemove', moveListener);
      window.removeEventListener('keydown', moveListener);
      clearInterval(interval);
    };
  }, [isPaused, disabledAutoPause, handlePauseClick]);

  // Calculate remaining Pause available idle time
  const remainingIdleTimeSeconds = pauseTimeClaimTimeExtensionInfo
    ? pauseTimeClaimTimeExtensionInfo.maxAvailableSeconds -
      pauseTimeClaimTimeExtensionInfo.usedSeconds
    : 0;

  const getTooltipText = () => {
    const hasExtensionConfigured =
      pauseTimerClaimTimeExtensionEnabled &&
      pauseTimeClaimTimeExtensionInfo &&
      pauseTimeClaimTimeExtensionInfo.maxAvailableSeconds > 0;

    return (
      <div>
        <span>
          {hasExtensionConfigured
            ? `Remaining Paused Time: ${formatTime(
                remainingIdleTimeSeconds,
                TIME_FORMAT_PRESETS.REMAINING_TIME,
              )}`
            : `Elapsed Paused Time: ${formatTime(
                totalPausedTimeSeconds,
                TIME_FORMAT_PRESETS.REMAINING_TIME,
              )}`}
        </span>
        <Divider className="my-2" />
        <span className="text-gray-400">
          Pause the timer when you are not actively working. Time efficiency affects your quality
          rating.
        </span>
      </div>
    );
  };
  if (!taskId) {
    return null;
  }

  return (
    <Tooltip
      text={<ToolTipText text={getTooltipText()} width={300} />}
      target={
        <Button
          onClick={handlePauseClick}
          disabled={isLoading}
          isLoading={isLoading}
          variant="white"
        >
          <div className="flex items-center gap-1">
            <span>{isPaused ? 'Resume task' : 'Pause task'}</span>
          </div>
        </Button>
      }
    />
  );
};

export default PauseButton;
